import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash } from "@phosphor-icons/react";

export default function PanelSourcesPage() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", login_url: "", data_url: "", username: "", password: "", selectors: "{}", notes: "" });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await API.get("/panel-sources");
      setSources(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    try {
      let selectors = {};
      try { selectors = JSON.parse(form.selectors); } catch {}
      const payload = { ...form, selectors };
      if (editing) {
        await API.put(`/panel-sources/${editing}`, payload);
      } else {
        await API.post("/panel-sources", payload);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: "", login_url: "", data_url: "", username: "", password: "", selectors: "{}", notes: "" });
      load();
    } catch (e) { alert("Save failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this panel source?")) return;
    try {
      await API.delete(`/panel-sources/${id}`);
      load();
    } catch (e) { alert("Delete failed"); }
  };

  const openEdit = (s) => {
    setEditing(s.id);
    setForm({ name: s.name, login_url: s.login_url, data_url: s.data_url, username: s.username, password: "", selectors: JSON.stringify(s.selectors || {}), notes: s.notes || "" });
    setShowForm(true);
  };

  return (
    <div data-testid="panel-sources-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Panel Sources</h1>
          <p className="text-sm text-zinc-500 mt-1">Configure web panel connections</p>
        </div>
        <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) { setEditing(null); setForm({ name: "", login_url: "", data_url: "", username: "", password: "", selectors: "{}", notes: "" }); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-panel-btn" className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-2"><Plus size={16} /> Add Source</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Add"} Panel Source</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <FormField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="panel-name" />
              <FormField label="Login URL" value={form.login_url} onChange={(v) => setForm({ ...form, login_url: v })} testId="panel-login-url" />
              <FormField label="Data URL" value={form.data_url} onChange={(v) => setForm({ ...form, data_url: v })} testId="panel-data-url" />
              <FormField label="Username" value={form.username} onChange={(v) => setForm({ ...form, username: v })} testId="panel-username" />
              <FormField label="Password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} type="password" testId="panel-password" />
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Selectors (JSON)</Label>
                <Textarea data-testid="panel-selectors" value={form.selectors} onChange={(e) => setForm({ ...form, selectors: e.target.value })} className="rounded-sm border-zinc-300 font-mono text-xs h-20" />
              </div>
              <FormField label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="panel-notes" />
              <Button data-testid="save-panel-btn" onClick={handleSubmit} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm">{editing ? "Update" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-100">
        {loading ? <div className="p-8 text-center text-sm text-zinc-400">Loading...</div> :
         sources.length === 0 ? <div className="p-8 text-center text-sm text-zinc-400">No panel sources configured</div> :
         sources.map((s) => (
          <div key={s.id} className="p-4 flex items-start justify-between hover:bg-zinc-50 transition-colors" data-testid={`panel-source-${s.id}`}>
            <div>
              <p className="text-sm font-semibold text-zinc-900">{s.name}</p>
              <p className="text-xs font-mono text-zinc-400 mt-0.5">{s.login_url}</p>
              <p className="text-xs text-zinc-500 mt-0.5">User: {s.username}</p>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => openEdit(s)} className="text-zinc-400 hover:text-zinc-900"><Pencil size={14} /></Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-zinc-400 hover:text-red-600"><Trash size={14} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm border-zinc-300" />
    </div>
  );
}
